/**
 * Sprint T-16.1 · Automation Center · JsonPreview.
 *
 * Truncated preview + click-to-expand modal for arbitrary JSON blobs.
 * Founder decision §17.3 · truncado inteligente + modal completo.
 */
import React, { useState } from "react";
import { X, Maximize2, Copy } from "lucide-react";
import { toast } from "sonner";

function _stringify(data) {
  if (data == null) return "";
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

function _truncate(str, maxChars) {
  if (!str) return "";
  if (str.length <= maxChars) return str;
  return str.slice(0, maxChars) + "…";
}

export default function JsonPreview({
  data,
  maxChars = 200,
  testId,
  label = "payload",
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const raw = _stringify(data);

  if (!raw || raw === "{}" || raw === "null") {
    return (
      <span data-testid={testId} className="text-[10px] text-[var(--ag-text-dim)] italic">
        vacío
      </span>
    );
  }

  const truncated = _truncate(raw.replace(/\s+/g, " "), maxChars);
  const isTruncated = raw.length > maxChars;

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(raw);
      toast.success("JSON copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <>
      <span data-testid={testId} className="inline-flex items-center gap-1">
        <code
          className={`font-mono text-[10px] text-[var(--ag-text-dim)] ${compact ? "" : "break-all"}`}
        >
          {truncated}
        </code>
        {isTruncated && (
          <button
            type="button"
            data-testid={testId ? `${testId}-expand` : "json-preview-expand"}
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-1 py-0.5 text-[9px] hover:bg-[var(--ag-surface-2)]"
            title="Ver JSON completo"
          >
            <Maximize2 className="h-2.5 w-2.5" /> Ver
          </button>
        )}
      </span>

      {open && (
        <div
          data-testid={testId ? `${testId}-modal` : "json-preview-modal"}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="max-h-[80vh] w-full max-w-3xl overflow-hidden rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-center justify-between border-b border-[var(--ag-border)] px-3 py-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ag-text-dim)]">
                {label}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  data-testid="json-preview-copy"
                  onClick={copyAll}
                  className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] hover:bg-[var(--ag-surface-2)]"
                >
                  <Copy className="h-3 w-3" /> Copiar
                </button>
                <button
                  type="button"
                  data-testid="json-preview-close"
                  onClick={() => setOpen(false)}
                  className="rounded border border-[var(--ag-border)] p-1 hover:bg-[var(--ag-surface-2)]"
                  aria-label="Cerrar"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </header>
            <pre className="max-h-[70vh] overflow-auto bg-[var(--ag-surface-2)] p-4 font-mono text-[11px] text-[var(--ag-text)]">
              {raw}
            </pre>
          </div>
        </div>
      )}
    </>
  );
}
